import React, { useEffect, useState } from 'react';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  CalendarOutlined,
  CheckOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DollarCircleOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SearchOutlined,
  TagOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
  CommentOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import API from '../../API/API';
import { ROUTES } from '../../Routes/Routes';
import InvoicePreviewModal from '../LeadDetails/components/InvoicePreviewModal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(quarterOfYear);

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const invoiceStatusOptions = ['INVOICE-SENT', 'INVOICE-NOT-SENT'];

const getQuickDateRange = (value) => {
  const now = dayjs();

  switch (value) {
    case 'today':
      return [now.startOf('day'), now.endOf('day')];
    case 'yesterday': {
      const yesterday = now.subtract(1, 'day');
      return [yesterday.startOf('day'), yesterday.endOf('day')];
    }
    case 'thisWeek':
      return [now.startOf('week'), now.endOf('week')];
    case 'thisMonth':
      return [now.startOf('month'), now.endOf('month')];
    case 'lastMonth': {
      const lastMonth = now.subtract(1, 'month');
      return [lastMonth.startOf('month'), lastMonth.endOf('month')];
    }
    case 'thisQuarter':
      return [now.startOf('quarter'), now.endOf('quarter')];
    case 'thisYear':
      return [now.startOf('year'), now.endOf('year')];
    default:
      return [now.startOf('month'), now.endOf('month')];
  }
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const toPST = (date) => {
  if (!date) return null;
  return dayjs(date).tz('America/Los_Angeles');
};

const getInvoiceStatusDate = (record) => {
  if (!Array.isArray(record?.invoiceStatusHistory) || record.invoiceStatusHistory.length === 0) {
    return null;
  }

  const latest = [...record.invoiceStatusHistory].sort(
    (a, b) => new Date(b.changedAt) - new Date(a.changedAt)
  )[0];

  return latest?.changedAt || null;
};

const getPaymentCompletedDate = (record) => {
  const yesEntries = Array.isArray(record?.payementAllowHistory)
    ? record.payementAllowHistory.filter((entry) => entry?.value === 'Yes' && entry?.changedAt)
    : [];

  if (yesEntries.length > 0) {
    const latestYesEntry = [...yesEntries].sort(
      (a, b) => new Date(b.changedAt) - new Date(a.changedAt)
    )[0];

    return latestYesEntry?.changedAt || null;
  }

  return record?.payementAllowUpdatedAt || null;
};

const getLastPaymentDate = (record) => {
  if (!Array.isArray(record?.advancePayments) || record.advancePayments.length === 0) {
    return null;
  }

  const latestPayment = [...record.advancePayments]
    .filter((payment) => payment?.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

  return latestPayment?.date || null;
};

const getAgentName = (record) => {
  if (record.assignedAgent?.name) return record.assignedAgent.name;
  if (record.agentName) return record.agentName;
  if (record.handledBy?.firstName && record.handledBy?.lastName) {
    return `${record.handledBy.firstName} ${record.handledBy.lastName}`;
  }
  if (record.handledBy?.name) return record.handledBy.name;
  return 'Unassigned';
};

const Invoice = ({ invoiceView = 'pending' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalRecords, setTotalRecords] = useState(0);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loginAgent, setLoginAgent] = useState({});
  const [searchText, setSearchText] = useState('');
  // The search a request uses: the box's text, set 0.5 s after typing pauses
  // (at once when the box is cleared), so a search is not sent per keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      setSearchQuery('');
      return undefined;
    }
    const timer = setTimeout(() => setSearchQuery(trimmed), 500);
    return () => clearTimeout(timer);
  }, [searchText]);
  const [searchFields, setSearchFields] = useSearchFields('invoice:search-fields');
  // The "Search in" group only changes the results of a search, so changing it
  // with an empty search box sends no request.
  const effectiveSearchFields = searchQuery ? searchFields : null;
  const [dateRange, setDateRange] = useState(() => {
    const now = dayjs();
    return [now.startOf('month'), now.endOf('month')];
  });
  const [quickDateFilter, setQuickDateFilter] = useState('thisMonth');
  const [filteredInfo, setFilteredInfo] = useState({
    invoiceStatus: null
  });
  const [selectedLeadForInvoice, setSelectedLeadForInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [editingInvoiceStatus, setEditingInvoiceStatus] = useState('');
  const [updatingInvoiceStatus, setUpdatingInvoiceStatus] = useState(false);
  const [statistics, setStatistics] = useState({
    totalEligible: 0,
    invoiceSent: 0,
    invoicePending: 0,
    totalPaid: 0,
    totalBalance: 0
  });
const [editingInvoiceDate, setEditingInvoiceDate] = useState(null);
const [savingInvoiceStatus, setSavingInvoiceStatus] = useState(false);
  const isAdmin = loginAgent?.role === 'ADMIN';
  const isCompletedView = invoiceView === 'completed';
  const fixedInvoiceStatus = isCompletedView ? 'INVOICE-SENT' : 'INVOICE-NOT-SENT';
  const pageTitle = isCompletedView ? 'Invoice Completed' : 'Invoice Pending';
  const emptyStateLabel = isCompletedView ? 'completed' : 'pending';
  const activeDateField = isCompletedView ? 'invoiceStatusUpdatedAt' : 'payementAllowUpdatedAt';
  const bannerDateLabel = isCompletedView ? 'invoice sent/completed date' : 'payment completed date';
const [serviceAgentsMap, setServiceAgentsMap] = useState({});
const [loadingServiceAgents, setLoadingServiceAgents] = useState(false);
const [selectedServiceAgentId, setSelectedServiceAgentId] = useState(null);
    const [commentsModalVisible, setCommentsModalVisible] = useState(false);
    const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);

const [serviceAgentModalVisible, setServiceAgentModalVisible] = useState(false);
const [selectedLeadForServiceAgent, setSelectedLeadForServiceAgent] = useState(null);
const [selectedServiceAgentForLead, setSelectedServiceAgentForLead] = useState(null);
const [savingServiceAgent, setSavingServiceAgent] = useState(false);
const [editingSpecification, setEditingSpecification] = useState(null);
const [editingSpecValue, setEditingSpecValue] = useState('');
const [updatingSpecification, setUpdatingSpecification] = useState(false);
 

const handleUpdateSpecification = async (leadId, newSpecification) => {
    if (!leadId) return;
    
    const trimmedValue = newSpecification?.trim() || '';
    
    if (trimmedValue.length > 2000) {
        message.warning('Specification cannot exceed 2000 characters');
        return;
    }
    
    setUpdatingSpecification(true);
    try {
        // First, get the current lead data to preserve other vehicleInfo fields
        const currentLead = leads.find(lead => lead._id === leadId);
        const currentVehicleInfo = currentLead?.vehicleInfo || {};
        
        // Merge the specification with existing vehicleInfo
        const payload = {
            vehicleInfo: {
                ...currentVehicleInfo, // Preserve all existing fields
                specification: trimmedValue // Update only specification
            }
        };
        
        console.log('Sending payload with preserved data:', payload);
        
        const response = await API.UpdateLead(leadId, payload);
        
        if (response?.status) {
            message.success('Specification updated successfully!');
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to update specification');
        }
    } catch (error) {
        console.error('Error updating specification:', error);
        message.error('Failed to update specification');
    } finally {
        setUpdatingSpecification(false);
        setEditingSpecification(null);
        setEditingSpecValue('');
    }
};
// Handle opening the service agent selection modal
const handleServiceAgentEdit = (record) => {
    const currentServiceAgentId = record.zohoServiceHandledBy?._id || record.zohoServiceHandledBy;
    setSelectedLeadForServiceAgent(record);
    setSelectedServiceAgentForLead(currentServiceAgentId || null);
    setServiceAgentModalVisible(true);
};

// Handle saving the service agent
const handleServiceAgentSave = async () => {
    if (!selectedLeadForServiceAgent) return;
    
    setSavingServiceAgent(true);
    
    try {
        const payload = {
            zohoServiceHandledBy: selectedServiceAgentForLead || null // Set to null if unassigned
        };
        
        const response = await API.UpdateLead(selectedLeadForServiceAgent._id, payload);
        
        if (response?.status || response?.success) {
            message.success(selectedServiceAgentForLead ? 'Service agent assigned successfully!' : 'Service agent unassigned successfully!');
            
            // Update local state
            setLeads(prevLeads => 
                prevLeads.map(lead => 
                    lead._id === selectedLeadForServiceAgent._id ? { 
                        ...lead, 
                        zohoServiceHandledBy: selectedServiceAgentForLead
                    } : lead
                )
            );
            
            // Close modal
            setServiceAgentModalVisible(false);
            setSelectedLeadForServiceAgent(null);
            setSelectedServiceAgentForLead(null);
            
            // Refresh leads to get populated agent data
            fetchLeads(true);
            fetchServiceAgents(); // Refresh the agents map
        } else {
            message.error(response?.message || 'Failed to assign service agent');
        }
    } catch (error) {
        console.error('Error assigning service agent:', error);
        message.error('Failed to save service agent assignment');
    } finally {
        setSavingServiceAgent(false);
    }
};

    const handleAddComment = async () => {
    if (!newComment.trim()) {
        message.warning('Please enter a comment');
        return;
    }

    if (!selectedLeadForComment) return;

    setAddingComment(true);
    try {
        const existingComments = selectedLeadForComment.invoicePageComment || [];
        const payload = {
            invoicePageComment: [
                ...existingComments,
                {
                    comment: newComment.trim(),
                    changedAt: new Date().toISOString()
                }
            ]
        };

        const response = await API.UpdateLead(selectedLeadForComment._id, payload);

        if (response?.status) {
            message.success('Comment added successfully!');
            setNewComment('');
            setCommentsModalVisible(false);
            setSelectedLeadForComment(null);
            // Refresh the leads list
            fetchLeads();
        } else {
            message.error(response?.message || 'Failed to add comment');
        }
    } catch (error) {
        console.error('Error adding comment:', error);
        message.error('Failed to add comment');
    } finally {
        setAddingComment(false);
    }
};
const formatCommentDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
};
  useEffect(() => {
    const storedUser = localStorage.getItem('userData');
    if (!storedUser) {
      navigate(ROUTES.LOGIN);
      return;
    }

    const parsedUser = JSON.parse(storedUser);
    setLoginAgent(parsedUser);

    if (parsedUser?.role === 'ADMIN') {
      fetchAgents();
    }
    fetchServiceAgents();
  }, [navigate]);
const getServiceAgentName = (record) => {
    const serviceAgentId = record.zohoServiceHandledBy?._id || record.zohoServiceHandledBy;
    if (!serviceAgentId) return null;
    
    // Check if already populated as object
    if (record.zohoServiceHandledBy && typeof record.zohoServiceHandledBy === 'object') {
        return `${record.zohoServiceHandledBy.firstName || ''} ${record.zohoServiceHandledBy.lastName || ''}`.trim() || 
               record.zohoServiceHandledBy.userName || 'Service Agent';
    }
    
    // Check in our map
    const agentInfo = serviceAgentsMap[serviceAgentId];
    return agentInfo?.name || null;
};

const getDocServiceAgentId = (record) => {
    const serviceAgent = record?.zohoServiceHandledBy;
    if (!serviceAgent) return null;
    if (typeof serviceAgent === 'object') {
        return serviceAgent._id || serviceAgent.id || serviceAgent.$oid || null;
    }
    return serviceAgent;
};

const hasDocServiceAgentAssigned = (record) => Boolean(getDocServiceAgentId(record));

const showMissingDocServiceAgentError = () => {
    message.error('Please assign the Doc Service-Agent');
};

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, effectiveSearchFields, dateRange, filteredInfo, selectedAgentId]);

  useEffect(() => {
    fetchLeads();
  }, [
    currentPage,
    pageSize,
    filteredInfo.invoiceStatus,
    searchQuery,
    effectiveSearchFields,
    dateRange,
    selectedAgentId,
    selectedServiceAgentId
  ]);

  const fetchAgents = async () => {
    try {
      const response = await API.userGetAll();
      if (response?.success) {
        const salesAgents = (response.data || []).filter((user) => user.role === 'SALES-AGENT');
        setAgents(salesAgents);
      }
    } catch (error) {
      console.error('Failed to fetch agents', error);
    }
  };
const fetchServiceAgents = async () => {
    setLoadingServiceAgents(true);
    try {
        const response = await API.userGetAll();
        if (response?.success) {
            const agentsMap = {};
            // Get all users with SERVICE-AGENT or SUPPORT-AGENT role
            const serviceAgentsList = (response.data || []).filter(
                user => user.role === "SERVICE-AGENT" || user.role === "SUPPORT-AGENT"
            );
            serviceAgentsList.forEach(agent => {
                agentsMap[agent._id] = {
                    name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.userName || agent.email,
                    email: agent.email,
                    phone: agent.directNumber,
                    role: agent.role
                };
            });
            setServiceAgentsMap(agentsMap);
        }
    } catch (error) {
        console.error("Failed to fetch service agents", error);
    } finally {
        setLoadingServiceAgents(false);
    }
};

  const calculateStatistics = (rows) => {
    const invoiceSent = rows.filter((lead) => lead.invoiceStatus === 'INVOICE-SENT').length;
    const totalPaid = rows.reduce((sum, lead) => sum + Number(lead.paidAmount || 0), 0);
    const totalBalance = rows.reduce((sum, lead) => sum + Number(lead.balanceAmount || 0), 0);

    setStatistics({
      totalEligible: rows.length,
      invoiceSent,
      invoicePending: rows.length - invoiceSent,
      totalPaid,
      totalBalance
    });
  };

  const fetchLeads = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const isSearching = Boolean(searchQuery);
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy: activeDateField,
        order: 'desc',
        status: 'Sales Made',
        payementAllow: 'Yes'
      };

      if (isSearching) {
        params.search = searchQuery;
        params.searchFields = searchFields;
      } else if (dateRange?.[0] && dateRange?.[1]) {
        params.dateField = activeDateField;
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      params.invoiceStatus = fixedInvoiceStatus;

      if (isAdmin && selectedAgentId) {
        params.agentId = selectedAgentId;
      }

      // The server sends only the fields this page uses
      // (LeadController LEAD_VIEW_FIELDS['invoice']).
      params.view = 'invoice';
      const response = await API.leadGetAll(params);

      if (response?.status) {
        const rows = response.data || [];
        setLeads(rows);
        calculateStatistics(rows);
        setTotalRecords(response.pagination?.totalRecords || rows.length);

        if (!isSearching && rows.length === 0) {
          message.info(`No ${emptyStateLabel} invoice leads found in the selected ${bannerDateLabel}`);
        }
      } else {
        setLeads([]);
        setTotalRecords(0);
        calculateStatistics([]);
      }
    } catch (error) {
      console.error('Failed to fetch invoice leads:', error);
      message.error('Failed to fetch invoice leads');
      setLeads([]);
      setTotalRecords(0);
      calculateStatistics([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchLeads(true);
  };

  const handleQuickDateFilter = (value) => {
    setQuickDateFilter(value);
    setDateRange(getQuickDateRange(value));
    setCurrentPage(1);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setQuickDateFilter('custom');
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedAgentId(null);
    setFilteredInfo({
      invoiceStatus: null
    });
    setQuickDateFilter('thisMonth');
    setDateRange(getQuickDateRange('thisMonth'));
    setCurrentPage(1);
    message.info({
      content: 'All invoice filters cleared',
      icon: <ClearOutlined />
    });
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleTableChange = (_, filters) => {
    setFilteredInfo({
      invoiceStatus: filters.invoiceStatus || null
    });
  };

const handleInvoiceStatusEditStart = (record) => {
  if (!hasDocServiceAgentAssigned(record)) {
    showMissingDocServiceAgentError();
    return;
  }

  const invoiceHistory = record.invoiceStatusHistory || [];
  const currentStatus = record.invoiceStatus || 'INVOICE-NOT-SENT';
  
  // ✅ Get the OLDEST entry with the current status
  const firstStatusEntry = [...invoiceHistory]
    .sort((a, b) => new Date(a.changedAt) - new Date(b.changedAt)) // Oldest first
    .find(entry => entry.status === currentStatus);
  
  const existingDate = firstStatusEntry?.changedAt || new Date();
  
  setEditingLeadId(record._id);
  setEditingInvoiceStatus(currentStatus);
  setEditingInvoiceDate(dayjs(existingDate));
};
 const handleInvoiceStatusEditCancel = () => {
  setEditingLeadId(null);
  setEditingInvoiceStatus('');
  setEditingInvoiceDate(null);
};


const handleInvoiceStatusChange = (record, value) => {
  if (!hasDocServiceAgentAssigned(record)) {
    setEditingInvoiceStatus(record?.invoiceStatus || 'INVOICE-NOT-SENT');
    showMissingDocServiceAgentError();
    return;
  }

  setEditingInvoiceStatus(value);
};


const handleInvoiceStatusSave = async (record) => {
  if (!hasDocServiceAgentAssigned(record)) {
    setEditingInvoiceStatus(record?.invoiceStatus || 'INVOICE-NOT-SENT');
    showMissingDocServiceAgentError();
    return;
  }

  if (!editingInvoiceDate) {
    message.warning('Please select a date');
    return;
  }

  try {
    setSavingInvoiceStatus(true);

    const existingHistory = record.invoiceStatusHistory || [];
    
    // Check if there's already an entry with this status
    const existingEntryIndex = existingHistory.findIndex(
      entry => entry.status === editingInvoiceStatus
    );
    
    let updatedHistory;
    
    if (existingEntryIndex !== -1) {
      // Update existing entry's date
      updatedHistory = [...existingHistory];
      updatedHistory[existingEntryIndex] = {
        ...updatedHistory[existingEntryIndex],
        changedAt: editingInvoiceDate.toISOString()
      };
    } else {
      // Add new entry
      const newHistoryEntry = {
        status: editingInvoiceStatus,
        changedAt: editingInvoiceDate.toISOString()
      };
      updatedHistory = [...existingHistory, newHistoryEntry];
    }
    
    const payload = {
      invoiceStatus: editingInvoiceStatus,
      invoiceStatusHistory: updatedHistory,
      _skipAutoInvoiceHistory: true  // ✅ Add this flag to skip auto-generation
    };

    const response = await API.UpdateLead(record._id, payload);

    if (response?.status || response?.success) {
      message.success('Invoice status updated successfully with date!');
      handleInvoiceStatusEditCancel();
      await fetchLeads(true);
    } else {
      message.error(response?.message || 'Failed to update invoice status');
    }
  } catch (error) {
    console.error('Failed to update invoice status:', error);
    message.error('Failed to update invoice status');
  } finally {
    setSavingInvoiceStatus(false);
  }
};
  const openInvoicePreview = (record) => {
    setSelectedLeadForInvoice(record);
    setShowInvoiceModal(true);
  };

  const handleViewLead = (record) => {
    navigate(`/lead-details/${record._id}`, {
      state: { from: location.pathname }
    });
  };

  const agentColumn = isAdmin
    ? [
        {
          title: 'Sales-Agent',
          key: 'agent',
          width: 160,
          render: (_, record) => {
            const agentName = getAgentName(record);
            const isUnassigned = agentName === 'Unassigned';

            return (
              <Tooltip title={agentName}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar
                    size={32}
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: isUnassigned ? '#d9d9d9' : '#722ed1',
                      flexShrink: 0
                    }}
                  >
                    {!isUnassigned && agentName?.[0]?.toUpperCase()}
                  </Avatar>
                  <Text
                    ellipsis={{ tooltip: true }}
                    style={{
                      fontSize: 13,
                      color: isUnassigned ? '#8c8c8c' : '#1f2937',
                      fontStyle: isUnassigned ? 'italic' : 'normal'
                    }}
                  >
                    {agentName}
                  </Text>
                </div>
              </Tooltip>
            );
          }
        }
      ]
    : [];

  const columns = [
       {
    title: 'Doc Service-Agent',
    key: 'serviceAgent',
    width: 180,
    sorter: (a, b) => {
        const nameA = getServiceAgentName(a) || '';
        const nameB = getServiceAgentName(b) || '';
        return nameA.localeCompare(nameB);
    },
    render: (_, record) => {
        const serviceAgentName = getServiceAgentName(record);
        const isAssigned = !!serviceAgentName;
        
        return (
            <div 
                style={{ cursor: 'pointer' }}
                onClick={() => handleServiceAgentEdit(record)}
            >
                <Tooltip title={isAssigned ? `Click to change service agent` : 'Click to assign service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#52c41a' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && serviceAgentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text
                                        ellipsis={{ tooltip: true }}
                                        style={{
                                            fontSize: 13,
                                            color: '#1f2937',
                                            display: 'block',
                                            fontWeight: 500
                                        }}
                                    >
                                        {serviceAgentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        Click to edit
                                    </Text>
                                </>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#1890ff',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    + Assign Agent
                                </Text>
                            )}
                        </div>
                        <EditOutlined 
                            style={{ 
                                fontSize: 12, 
                                color: '#1890ff',
                                opacity: 0.6
                            }} 
                        />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
    {
      title: 'Order Number',
      key: 'orderNumber',
      width: 150,
      render: (_, record) => {
        const hasOrderNumber = Boolean(record.orderNumber);

        return (
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: hasOrderNumber ? '#f0f0ff' : '#fff1f0',
                padding: '4px 8px',
                borderRadius: 8
              }}
            >
              <FileTextOutlined style={{ color: hasOrderNumber ? '#722ed1' : '#ff4d4f', fontSize: 12 }} />
              <Text
                style={{
                  color: hasOrderNumber ? '#722ed1' : '#ff4d4f',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  fontStyle: hasOrderNumber ? 'normal' : 'italic'
                }}
              >
                {hasOrderNumber ? `#${record.orderNumber.slice(-8)}` : 'Pending'}
              </Text>
            </div>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 4 }}>
              <CalendarOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
              <Tooltip title={dayjs(record.orderDate || record.createdAt).format('MMM DD, YYYY hh:mm A')}>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {record.orderDate
                    ? dayjs(record.orderDate).format('MM/DD/YY hh:mm A')
                    : dayjs(record.createdAt).format('MM/DD/YY hh:mm A')}
                </Text>
              </Tooltip>
            </div>
          </div>
        );
      }
    },
    ...agentColumn,
    {
      title: 'Customer',
      key: 'customer',
      width: 280,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
          <Avatar
            size={38}
            icon={<UserOutlined />}
            style={{
              background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
              boxShadow: '0 4px 12px rgba(24,144,255,0.2)',
              flexShrink: 0
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              ellipsis={{ tooltip: record.personalInfo?.fullName || 'N/A' }}
              style={{ fontSize: 14, display: 'block', marginBottom: 4 }}
            >
              {record.personalInfo?.fullName || 'N/A'}
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <MailOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                <Text
                  type="secondary"
                  ellipsis={{ tooltip: record.personalInfo?.email || 'N/A' }}
                  style={{ fontSize: 12 }}
                >
                  {record.personalInfo?.email || 'N/A'}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <PhoneOutlined style={{ color: '#52c41a', fontSize: 11 }} />
                <Text
                  type="secondary"
                  ellipsis={{ tooltip: record.personalInfo?.phoneNumber || 'N/A' }}
                  style={{ fontSize: 12 }}
                >
                  {record.personalInfo?.phoneNumber || 'N/A'}
                </Text>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Vehicle & Part',
      key: 'vehicle',
      width: 230,
      render: (_, record) => (
        <Card
          size="small"
          style={{ borderRadius: 10, background: '#fafafa', border: 'none' }}
          bodyStyle={{ padding: '8px 10px' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
              <CarOutlined style={{ color: '#faad14', fontSize: 12, marginTop: 2, flexShrink: 0 }} />
              <Text strong style={{ fontSize: 13, display: 'block', lineHeight: 1.25, wordBreak: 'break-word' }}>
                {record.vehicleInfo?.year || ''} {record.vehicleInfo?.make || ''}
              </Text>
            </div>
            <Text
              type="secondary"
              style={{ fontSize: 12, marginLeft: 16, display: 'block', lineHeight: 1.25, wordBreak: 'break-word' }}
            >
              {record.vehicleInfo?.model || 'N/A'}
            </Text>
            <div style={{ marginTop: 6, borderTop: '1px dashed #e8e8e8', paddingTop: 6 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                <TagOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                <Tag color="cyan" style={{ fontSize: 11, whiteSpace: 'normal', lineHeight: 1.2, marginRight: 0 }}>
                  {record.vehicleInfo?.part || 'N/A'}
                </Tag>
              </div>
            </div>
          </div>
        </Card>
      )
    },
        {
    title: 'Specification',
    key: 'specification',
    width: 250,
    render: (_, record) => {
        const specification = record.vehicleInfo?.specification || '';
        const isEditing = editingSpecification === record._id;
        
        if (isEditing) {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={editingSpecValue}
                        onChange={(e) => setEditingSpecValue(e.target.value)}
                        placeholder="Enter specification details..."
                        style={{ flex: 1, fontSize: '12px' }}
                        autoFocus
                        maxLength={2000}
                        showCount
                    />
                    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={updatingSpecification}
                            onClick={() => handleUpdateSpecification(record._id, editingSpecValue)}
                            style={{ minWidth: '32px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setEditingSpecification(null);
                                setEditingSpecValue('');
                            }}
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                style={{ 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.3s',
                    backgroundColor: specification ? 'transparent' : '#fafafa',
                    border: '1px solid transparent',
                    minHeight: '60px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = specification ? 'transparent' : '#fafafa';
                    e.currentTarget.style.borderColor = 'transparent';
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditingSpecification(record._id);
                    setEditingSpecValue(specification);
                }}
            >
                {specification ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <FileTextOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>Specification:</Text>
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            display: 'block',
                            lineHeight: '1.5'
                        }}>
                            {specification.length > 100 
                                ? `${specification.substring(0, 100)}...` 
                                : specification}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EditOutlined style={{ marginRight: '4px' }} />
                                Click to edit
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <FileTextOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                            <EditOutlined style={{ marginRight: '4px', fontSize: '10px' }} />
                            Click to add specification
                        </Text>
                    </div>
                )}
            </div>
        );
    },
}, 
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      width: 190,
      render: (_, record) => {
        const paymentCompletedDate = getPaymentCompletedDate(record);

        return (
          <div>
            <Tag color="green" style={{ borderRadius: 16, padding: '4px 10px', fontWeight: 600, marginRight: 0 }}>
              Payment Completed
            </Tag>
            {paymentCompletedDate && (
              <div style={{ marginTop: 6 }}>
                <Tooltip title={toPST(paymentCompletedDate)?.format('MM/DD/YYYY hh:mm A [PST]')}>
                  <Space size={4}>
                    <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      Completed on {toPST(paymentCompletedDate)?.format('MM/DD/YY hh:mm A')}
                    </Text>
                  </Space>
                </Tooltip>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Payment',
      key: 'paymentSummary',
      width: 220,
      render: (_, record) => {
        const lastPaymentDate = getLastPaymentDate(record);

        return (
          <div
            style={{
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 12,
              padding: 12
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Total</Text>
              <Text strong style={{ fontSize: 12 }}>{formatCurrency(record.totalAmount)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Paid</Text>
              <Text strong style={{ fontSize: 12, color: '#52c41a' }}>{formatCurrency(record.paidAmount)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Balance</Text>
              <Text strong style={{ fontSize: 12, color: record.balanceAmount > 0 ? '#fa8c16' : '#1677ff' }}>
                {formatCurrency(record.balanceAmount)}
              </Text>
            </div>
            {lastPaymentDate && (
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: '1px dashed #e8e8e8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Tooltip title={toPST(lastPaymentDate)?.format('MM/DD/YYYY hh:mm A [PST]')}>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Last payment {toPST(lastPaymentDate)?.format('MM/DD/YY hh:mm A')}
                  </Text>
                </Tooltip>
              </div>
            )}
          </div>
        );
      }
    },
{
  title: 'Invoice Status',
  key: 'invoiceStatus',
  width: 240,
  fixed: 'right',
  render: (_, record) => {
    const isEditing = editingLeadId === record._id;
    const status = record.invoiceStatus || 'INVOICE-NOT-SENT';
    const invoiceHistory = record.invoiceStatusHistory || [];
    
    // ✅ Get the OLDEST (first) entry with the current status
    const firstStatusEntry = [...invoiceHistory]
      .sort((a, b) => new Date(a.changedAt) - new Date(b.changedAt)) // Oldest first
      .find(entry => entry.status === status);
    
    const invoiceStatusDate = firstStatusEntry?.changedAt || null;
    const isSent = status === 'INVOICE-SENT';

    if (isEditing) {
      return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Select
            value={editingInvoiceStatus}
            onChange={(value) => handleInvoiceStatusChange(record, value)}
            style={{ width: '100%' }}
            size="small"
          >
            {invoiceStatusOptions.map((option) => (
              <Option key={option} value={option}>
                {option === 'INVOICE-SENT' ? '✓ Invoice Sent' : '⚠ Invoice Not Sent'}
              </Option>
            ))}
          </Select>
          <DatePicker
            value={editingInvoiceDate}
            onChange={setEditingInvoiceDate}
            format="MM/DD/YYYY"
            size="small"
            placeholder="Select date"
            style={{ width: '100%' }}
          />
          <Space size={6}>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={savingInvoiceStatus}
              onClick={() => handleInvoiceStatusSave(record)}
              style={{ borderRadius: 6 }}
            />
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={handleInvoiceStatusEditCancel}
              disabled={savingInvoiceStatus}
              style={{ borderRadius: 6 }}
            />
          </Space>
        </Space>
      );
    }

    return (
      <div>
        <Space size={4}>
          <Tag
            color={isSent ? 'success' : 'warning'}
            style={{ padding: '4px 12px', borderRadius: 16, fontWeight: 500, marginRight: 0, cursor: 'pointer' }}
            onClick={() => handleInvoiceStatusEditStart(record)}
          >
            {isSent ? 'Sent' : 'Not Sent'}
          </Tag>
          <Tooltip title="Edit Invoice Status with Date">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleInvoiceStatusEditStart(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
        {invoiceStatusDate && (
          <div style={{ marginTop: 6 }}>
            <Tooltip title={toPST(invoiceStatusDate)?.format('MM/DD/YYYY hh:mm A [PST]')}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Since: {toPST(invoiceStatusDate)?.format('MM/DD/YY hh:mm A')}
                </Text>
              </Space>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
},
 
{
    title: 'Comment',
    key: 'invoicePageComment',
    width: 280,
    render: (_, record) => {
        const comments = record.invoicePageComment || [];
        const latestComment = comments.length > 0 ? comments[comments.length - 1] : null;
        
        return (
            <div>
             

                {/* Display Latest Comment */}
                {latestComment ? (
                    <div 
                        style={{
                            background: '#fafafa',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            wordBreak: 'break-word',      // Add this
                            wordWrap: 'break-word',       // Add this
                            overflowWrap: 'break-word',   // Add this
                            whiteSpace: 'normal'          // Add this
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                {formatCommentDate(latestComment.changedAt)}
                            </Text>
                            {comments.length > 1 && (
                                <Tag 
                                    color="blue" 
                                    style={{ 
                                        fontSize: '10px', 
                                        padding: '0 4px', 
                                        margin: 0,
                                        cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLeadForComment(record);
                                        setCommentsModalVisible(true);
                                    }}
                                >
                                    +{comments.length - 1} more
                                </Tag>
                            )}
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            display: 'block', 
                            wordBreak: 'break-word',    // Add this
                            whiteSpace: 'pre-wrap'       // Add this for preserving whitespace and wrapping
                        }}>
                            {latestComment.comment.length > 80 
                                ? `${latestComment.comment.substring(0, 80)}...` 
                                : latestComment.comment}
                        </Text>
                    </div>
                ) : (
                    <div 
                        style={{ 
                            textAlign: 'center', 
                            padding: '12px',
                            background: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <CommentOutlined style={{ marginRight: '4px', fontSize: '14px' }} />
                            No comments yet
                        </Text>
                    </div>
                )}
                   <Button
                    type="primary"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLeadForComment(record);
                        setNewComment('');
                        setCommentsModalVisible(true);
                    }}
                    style={{ 
                        marginBottom: '12px',
                        width: '100%',
                        fontSize: '12px'
                    }}
                >
                    Add Comment
                </Button>
            </div>
        );
    },
},
    {
      title: 'Actions',
      key: 'actions',
      width: 170,
      fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            size="small"
            onClick={() => openInvoicePreview(record)}
            style={{ borderRadius: 8, fontSize: 12, height: 30, padding: '0 10px' }}
          >
            View Invoice
          </Button>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewLead(record)}
            style={{ borderRadius: 8, fontSize: 12, height: 30, padding: '0 10px' }}
          >
            Lead Details
          </Button>
        </Space>
      )
    }
  ];

  const isSearching = searchText?.trim().length > 0;

  return (
    <div style={{ padding: '24px', background: '#f5f7fb', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>{pageTitle}</Title>
          <Text type="secondary">
            Paid leads from the Pending Payment flow using the existing invoice preview flow from Lead Details.
          </Text>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12} xl={5}>
            <Card bordered={false} style={{ borderRadius: 18, boxShadow: '0 6px 16px rgba(15, 23, 42, 0.06)' }}>
              <Statistic title="Payment Completed Leads" value={statistics.totalEligible} prefix={<FileTextOutlined />} />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={5}>
            <Card bordered={false} style={{ borderRadius: 18, boxShadow: '0 6px 16px rgba(15, 23, 42, 0.06)' }}>
              <Statistic title="Invoice Sent" value={statistics.invoiceSent} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={7}>
            <Card bordered={false} style={{ borderRadius: 18, boxShadow: '0 6px 16px rgba(15, 23, 42, 0.06)' }}>
              <Statistic title="Total Paid" value={statistics.totalPaid} precision={2} prefix={<DollarCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={7}>
            <Card bordered={false} style={{ borderRadius: 18, boxShadow: '0 6px 16px rgba(15, 23, 42, 0.06)' }}>
              <Statistic title="Outstanding Balance" value={statistics.totalBalance} precision={2} prefix={<WalletOutlined />} valueStyle={{ color: statistics.totalBalance > 0 ? '#fa8c16' : '#1677ff' }} />
            </Card>
          </Col>
        </Row>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card
          bordered={false}
          style={{
            borderRadius: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            marginBottom: 24,
            border: '1px solid #f0f0f0'
          }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <Space.Compact block>
                <SearchFieldsSelect value={searchFields} onChange={setSearchFields} />
                <Input
                  placeholder={getSearchFieldsPlaceholder(searchFields)}
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  allowClear
                  size="large"
                />
              </Space.Compact>
            </Col>
            {isAdmin && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by Agent"
                  value={selectedAgentId}
                  onChange={setSelectedAgentId}
                  style={{ width: '100%', borderRadius: 12 }}
                  size="large"
                  allowClear
                  suffixIcon={<TeamOutlined />}
                  showSearch
                  optionFilterProp="children"
                >
                  {agents.map((agent) => (
                    <Option key={agent._id} value={agent._id}>
                      <Space>
                        <Avatar size="small" icon={<UserOutlined />} />
                        <span>{agent.firstName} {agent.lastName}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col xs={24} md={4}>
              <Select
                placeholder="Quick Date"
                style={{ width: '100%', borderRadius: 12 }}
                onChange={handleQuickDateFilter}
                value={quickDateFilter}
                size="large"
              >
                <Option value="today">Today</Option>
                <Option value="yesterday">Yesterday</Option>
                <Option value="thisWeek">This Week</Option>
                <Option value="thisMonth">This Month</Option>
                <Option value="lastMonth">Last Month</Option>
                <Option value="thisQuarter">This Quarter</Option>
                <Option value="thisYear">This Year</Option>
                <Option value="custom">Custom Range</Option>
              </Select>
            </Col>
            <Col xs={24} md={isAdmin ? 7 : 8}>
              <RangePicker
                style={{ width: '100%', borderRadius: 12 }}
                onChange={handleDateRangeChange}
                value={dateRange}
                size="large"
                format="MM/DD/YYYY"
                placeholder={['Start Date', 'End Date']}
              />
            </Col>
            <Col xs={24} md={isAdmin ? 4 : 6}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Tooltip title="Refresh">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    loading={refreshing}
                    size="large"
                    style={{ borderRadius: 12 }}
                  />
                </Tooltip>
                <Tooltip title="Clear Filters">
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                    size="large"
                    style={{ borderRadius: 12 }}
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>

          {dateRange?.[0] && dateRange?.[1] && (
            <div
              style={{
                marginTop: 16,
                backgroundColor: '#f0f5ff',
                padding: '8px 12px',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <CalendarOutlined style={{ color: '#1890ff' }} />
              <Text type="secondary">
                Showing paid invoice leads from {bannerDateLabel}:{' '}
                <Text strong>{dateRange[0].format('MM/DD/YYYY')}</Text> to{' '}
                <Text strong>{dateRange[1].format('MM/DD/YYYY')}</Text>
              </Text>
              <Badge count={totalRecords} showZero color="#52c41a" style={{ marginLeft: 'auto' }} />
            </div>
          )}
        </Card>
      </motion.div>

      <div
        style={{
          padding: '16px 20px',
          background: '#ffffff',
          borderTop: '1px solid #f0f0f0',
          borderBottom: '1px solid #f0f0f0',
          borderRadius: '12px 12px 0 0',
          marginTop: 8
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space size={16}>
              <Text strong style={{ fontSize: 16 }}>
                {isSearching ? (
                  <>
                    <SearchOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                    Search Results
                  </>
                ) : (
                  <>
                    <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    {dateRange?.[0] && dateRange?.[1]
                      ? `${dateRange[0].format('MMM D')} -> ${dateRange[1].format('MM/DD/YYYY')}`
                      : 'Selected Period'}
                  </>
                )}
              </Text>
              <Badge
                count={leads.length}
                showZero
                style={{
                  backgroundColor: '#52c41a',
                  fontSize: 12,
                  fontWeight: 'bold'
                }}
              />
              <Text type="secondary">
                of {totalRecords.toLocaleString()} total records
              </Text>
            </Space>
          </Col>
          <Col>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={handlePaginationChange}
              size="small"
              simple
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
            />
          </Col>
        </Row>
      </div>

      <Card
        bordered={false}
        style={{
          borderRadius: '0 0 20px 20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          borderTop: 'none'
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={columns}
          dataSource={leads}
          rowKey="_id"
          loading={loading}
          pagination={false}
          onChange={handleTableChange}
          scroll={{ x: isAdmin ? 1650 : 1490, y: 'calc(100vh - 540px)' }}
          bordered={false}
          size="middle"
          rowClassName={() => 'table-row-hover'}
          style={{ background: 'transparent' }}
        />

        {leads.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 32,
              padding: '20px 24px',
              background: '#fafafa',
              borderRadius: 12,
              margin: 24
            }}
          >
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={handlePaginationChange}
              showQuickJumper
              showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} invoice leads`}
              size="default"
            />
          </div>
        )}
      </Card>

      <InvoicePreviewModal
        visible={showInvoiceModal}
        onCancel={() => {
          setShowInvoiceModal(false);
          setSelectedLeadForInvoice(null);
        }}
        lead={selectedLeadForInvoice}
      />
        <Modal
   
    open={commentsModalVisible}
    onCancel={() => {
        setCommentsModalVisible(false);
        setSelectedLeadForComment(null);
        setNewComment('');
    }}
    footer={[
        <Button key="cancel" onClick={() => {
            setCommentsModalVisible(false);
            setSelectedLeadForComment(null);
            setNewComment('');
        }}>
            Cancel
        </Button>,
        <Button 
            key="submit" 
            type="primary" 
            onClick={handleAddComment}
            loading={addingComment}
            disabled={!newComment.trim()}
        >
            Add Comment
        </Button>
    ]}
    width={500}
>
    <div style={{ padding: '16px 0' }}>
      

        <div>
            <Text strong>Comment:</Text>
            <Input.TextArea
                placeholder="Enter your comment here..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={4}
                maxLength={1000}
                showCount
                style={{ marginTop: '8px' }}
                autoFocus
            />
        </div>

        {/* Show existing comments if any */}
        {selectedLeadForComment?.invoicePageComment?.length > 0 && (
            <div style={{ marginTop: '20px' }}>
                <Divider />
                <Text strong>Previous Comments ({selectedLeadForComment.invoicePageComment.length})</Text>
                <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    marginTop: '8px',
                    padding: '8px',
                    background: '#f5f5f5',
                    borderRadius: '8px'
                }}>
                    {[...selectedLeadForComment.invoicePageComment]
                        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                        .map((comment, idx) => (
                            <div key={idx} style={{ 
                                marginBottom: '12px', 
                                padding: '8px',
                                background: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e8e8e8'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                        {formatCommentDate(comment.changedAt)}
                                    </Text>
                                </div>
                                <Text style={{ fontSize: '13px' }}>{comment.comment}</Text>
                            </div>
                        ))}
                </div>
            </div>
        )}
    </div>
</Modal>
{/* Service Agent Assignment Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <TeamOutlined style={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
                <Title level={4} style={{ margin: 0 }}>Assign Service Agent</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Select service agent for this lead
                </Text>
            </div>
        </div>
    }
    open={serviceAgentModalVisible}
    onCancel={() => {
        setServiceAgentModalVisible(false);
        setSelectedLeadForServiceAgent(null);
        setSelectedServiceAgentForLead(null);
    }}
    footer={null}
    width={500}
    centered
>
    <div style={{ padding: '16px 0' }}>
        {selectedLeadForServiceAgent && (
            <>
                {/* Customer Info Card */}
                <Card 
                    size="small" 
                    style={{ 
                        marginBottom: 20,
                        background: '#f5f5f5',
                        borderRadius: 12,
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong>
                            {selectedLeadForServiceAgent.personalInfo?.fullName || 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {selectedLeadForServiceAgent.vehicleInfo?.make} {selectedLeadForServiceAgent.vehicleInfo?.model} - {selectedLeadForServiceAgent.vehicleInfo?.part}
                        </Text>
                        {selectedLeadForServiceAgent.zohoServiceHandledBy && (
                            <div style={{ 
                                marginTop: 8,
                                paddingTop: 8,
                                borderTop: '1px solid #e8e8e8'
                            }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Currently assigned: 
                                </Text>
                                <Text strong style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                                    {getServiceAgentName(selectedLeadForServiceAgent)}
                                </Text>
                            </div>
                        )}
                    </Space>
                </Card>

                {/* Service Agent Selection */}
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Select Service Agent:
                    </Text>
                    <Select
                        value={selectedServiceAgentForLead}
                        onChange={setSelectedServiceAgentForLead}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="Choose an agent"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        loading={loadingServiceAgents}
                    >
                        {Object.entries(serviceAgentsMap).map(([agentId, agentInfo]) => (
                            <Option key={agentId} value={agentId}>
                                <Space direction="vertical" size={0}>
                                    <Text strong>{agentInfo.name}</Text>
                                 
                                </Space>
                            </Option>
                        ))}
                    </Select>
                    <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                        {selectedServiceAgentForLead ? 'Agent will be assigned to this lead' : 'Clear to unassign the current agent'}
                    </Text>
                </div>

                {/* Modal Actions */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <Button 
                        onClick={() => {
                            setServiceAgentModalVisible(false);
                            setSelectedLeadForServiceAgent(null);
                            setSelectedServiceAgentForLead(null);
                        }} 
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleServiceAgentSave}
                        loading={savingServiceAgent}
                        size="large"
                        icon={<CheckOutlined />}
                    >
                        {selectedServiceAgentForLead ? 'Assign Agent' : 'Unassign Agent'}
                    </Button>
                </div>
            </>
        )}
    </div>
</Modal>
    </div>
  );
};

export default Invoice;


